import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {AgentRole} from 'Users/models/User/AgentUserType';
import {
  RecruiterCode,
  isValidRecruiterCode,
  ValidRecruiterCode,
  UnvalidatedRecruiterCode,
} from 'Users/models/User/Agent/RecruiterCode';

export class UnvalidatedFieldSalesLeaderUserType {
  public readonly key = 'UnvalidatedFieldSalesLeaderUserType';
  public readonly agentRole = AgentRole.FieldSalesLeader;

  constructor(
    public readonly recruiterCode: RecruiterCode,
    public readonly hasAhlAccess: boolean,
    public readonly hasNewYorkAccess: boolean
  ) {}
}

export class InvalidFieldSalesLeaderUserType {
  public readonly key = 'InvalidFieldSalesLeaderUserType';
  public readonly agentRole = AgentRole.FieldSalesLeader;

  constructor(
    public readonly recruiterCode: RecruiterCode,
    public readonly hasAhlAccess: boolean,
    public readonly hasNewYorkAccess: boolean
  ) {}
}

export class ValidFieldSalesLeaderUserType {
  public static create(recruiterCode: RecruiterCode, hasAhlAccess: boolean, hasNewYorkAccess: boolean) {
    const hasAtLeastOneAccess = hasAhlAccess || hasNewYorkAccess;

    return isValidRecruiterCode(recruiterCode) && hasAtLeastOneAccess
      ? new ValidFieldSalesLeaderUserType(recruiterCode, hasAhlAccess, hasNewYorkAccess)
      : new InvalidFieldSalesLeaderUserType(recruiterCode, hasAhlAccess, hasNewYorkAccess);
  }

  public readonly key = 'ValidFieldSalesLeaderUserType';
  public readonly agentRole = AgentRole.FieldSalesLeader;

  private constructor(
    public readonly recruiterCode: ValidRecruiterCode,
    public readonly hasAhlAccess: boolean,
    public readonly hasNewYorkAccess: boolean
  ) {}
}

export const changeRecruiterCode = (
  recruiterCode: string,
  isAhlRecruiterCode: boolean,
  isNewYorkRecruiterCode: boolean
) =>
  ValidFieldSalesLeaderUserType.create(
    ValidRecruiterCode.create(recruiterCode, isAhlRecruiterCode, isNewYorkRecruiterCode),
    isAhlRecruiterCode,
    isNewYorkRecruiterCode
  );
export const changeHasAhlAccess = (hasAhlAccess: boolean) => ({
  recruiterCode,
  hasNewYorkAccess,
}: FieldSalesLeaderUserType) => ValidFieldSalesLeaderUserType.create(recruiterCode, hasAhlAccess, hasNewYorkAccess);
export const changeNewYorkAccess = (hasNewYorkAccess: boolean) => ({
  recruiterCode,
  hasAhlAccess,
}: FieldSalesLeaderUserType) => ValidFieldSalesLeaderUserType.create(recruiterCode, hasAhlAccess, hasNewYorkAccess);

export const EmptyFieldSalesLeaderUserType = new UnvalidatedFieldSalesLeaderUserType(
  new UnvalidatedRecruiterCode('', false, false),
  false,
  false
);

export type FieldSalesLeaderUserType =
  | ValidFieldSalesLeaderUserType
  | InvalidFieldSalesLeaderUserType
  | UnvalidatedFieldSalesLeaderUserType;

export type FieldSalesLeaderUserTypeOptions<TReturn> = ToMatchOptions<FieldSalesLeaderUserType, 'key', TReturn>;

export const matchFieldSalesLeaderUserType = <TReturn>(
  fslUserType: FieldSalesLeaderUserType,
  options: FieldSalesLeaderUserTypeOptions<TReturn>
): TReturn => {
  switch (fslUserType.key) {
    case 'InvalidFieldSalesLeaderUserType':
      return options.InvalidFieldSalesLeaderUserType(fslUserType);
    case 'UnvalidatedFieldSalesLeaderUserType':
      return options.UnvalidatedFieldSalesLeaderUserType(fslUserType);
    case 'ValidFieldSalesLeaderUserType':
      return options.ValidFieldSalesLeaderUserType(fslUserType);
  }
};
